// SQ-AI composer — the input box from sq_context_picker_mock.html. The picker
// (chip row + popovers) sits at the top of the box, the textarea below, the send
// button bottom-right. The amber note + disclaimer render under the box.
//
// Enter sends; Shift+Enter inserts a newline. While a turn is in flight the send
// button becomes a stop control.

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "../sqAi.module.css";

function Composer({ onSubmit, onCancel, isInFlight, disabled, picker, note, onDismissNote }, ref) {
  const [text, setText] = useState("");
  const textareaRef = useRef(null);

  // A question the page hands back to the reader (RSM6 — P4, R3-3b): put in the box ready to send, never over what they have typed.
  useImperativeHandle(ref, () => ({
    putBack(question) {
      setText((typed) => (typed.trim() ? typed : question));
    },
  }), []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 180) + "px";
    }
  }, [text]);

  const submit = () => {
    if (!text.trim() || isInFlight || disabled) return;
    onSubmit(text.trim());
    setText("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = text.trim() && !isInFlight && !disabled;

  return (
    <div className={styles.composerWrap}>
      <div className={`${styles.box} ${isInFlight ? styles.working : ""}`}>
        {picker}
        <textarea
          ref={textareaRef}
          className={styles.ta}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Describe what you want to do…"
          rows={1}
          disabled={disabled}
        />
        <div className={styles.foot}>
          <button
            type="button"
            className={`${styles.icBtn} ${isInFlight ? styles.sendStop : styles.send}`}
            onClick={isInFlight ? onCancel : submit}
            disabled={!isInFlight && !canSend}
            title={isInFlight ? "Stop" : "Send"}
            aria-label={isInFlight ? "Stop" : "Send"}
          >
            {isInFlight ? "■" : "➤"}
          </button>
        </div>
      </div>

      {note && (
        <div className={styles.note}>
          <span aria-hidden="true">ⓘ</span>
          <span>{note}</span>
          {onDismissNote && (
            <button type="button" className={styles.noteX} onClick={onDismissNote} aria-label="Dismiss">
              ✕
            </button>
          )}
        </div>
      )}

      <div className={styles.disclaimer}>AI can make mistakes. Please double-check all responses.</div>
    </div>
  );
}

export default forwardRef(Composer);
